import * as React from 'react';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import BoxDefault from '../../../components/Box';
import styled from '../../../styled-components';
import { ApplicationState } from '../../../types/api';
import { Candidate, HiringManager, Question, Vacancy } from '../types';
import AddCandidateButton from './AddCandidateButton';
import DeleteVacancyButton from './DeleteVacancyButton';
import InviteAllCandidatesButton from './InviteAllCandidatesButton';
import ShareVacancyButton from './ShareVacancyButton';
import ShareWithHiringManagerButton from './ShareWithHiringManagerButton';
import VacancyCandidate from './VacancyCandidate';
import VacancyQuestion from './VacancyQuestion';

const Candidates = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 2em;
`;

const Name = styled.input`
    font-size: 2em;
    font-weight: bold;
    align-self: flex-start;
`;

const Details = styled.div`
    margin-top: 0.3em;
    margin-bottom: -1em;
    text-align: right;
    color: ${(props) => props.theme.gray};
`;

const Box = BoxDefault.extend`
    padding: 2em;
`;

interface RowProps {
    justify?: string;
}

const Row = styled<RowProps, any>('div')`
    display: flex;
    justify-content: ${(props) => (props.justify ? props.justify : 'space-between')};
`;

interface VacancyBoxProps {
    vacancy: Vacancy;
    onUpdate: (vacancy: Vacancy) => void;
    onDelete: (vacancy: Vacancy) => void;
    onAddCandidate: (vacancyId: string, firstname: string, lastname: string, email: string) => void;
    onUpdateCandidate: (vacancyId: string, candidate: Candidate) => void;
    onDeleteCandidate: (vacancyId: string, candidate: Candidate) => void;
    onInviteCandidate: (vacancyId: string, candidate: Candidate) => void;
    onShareWithHiringManager: (vacancy: Vacancy) => void;
    onAddVacancyLink: (vacancy: Vacancy) => void;
}

class VacancyBox extends React.Component<VacancyBoxProps, any> {
    constructor(props: VacancyBoxProps) {
        super(props);

        this.handleOnDelete = this.handleOnDelete.bind(this);
        this.handleOnChangeName = this.handleOnChangeName.bind(this);
        this.handleOnAddQuestion = this.handleOnAddQuestion.bind(this);
        this.handleOnChangeQuestion = this.handleOnChangeQuestion.bind(this);
        this.handleOnAddCandidate = this.handleOnAddCandidate.bind(this);
        this.handleOnUpdateCandidate = this.handleOnUpdateCandidate.bind(this);
        this.handleOnDeleteCandidate = this.handleOnDeleteCandidate.bind(this);
        this.handleOnInviteCandidate = this.handleOnInviteCandidate.bind(this);
        this.handleOnShareWithHiringManager = this.handleOnShareWithHiringManager.bind(this);
        this.handleOnUpdateHiringMangers = this.handleOnUpdateHiringMangers.bind(this);
        this.handleOnInviteAllCandidates = this.handleOnInviteAllCandidates.bind(this);
        this.sharedWithHiringManagerText = this.sharedWithHiringManagerText.bind(this);
        this.handleOnAddVacancyLink = this.handleOnAddVacancyLink.bind(this);
    }

    private handleOnDelete() {
        this.props.onDelete(this.props.vacancy);
    }

    private handleOnChangeName(event) {
        const name = event.target.value;
        if (this.props.vacancy.name !== name) {
            const vacancy: Vacancy = Object.assign({}, this.props.vacancy, {
                name,
            });
            this.props.onUpdate(vacancy);
        }
    }

    private handleOnAddQuestion(text: string) {
        // insert question
        const vacancy: Vacancy = Object.assign({}, this.props.vacancy, {
            questions: [
                ...this.props.vacancy.questions,
                { id: uuid(), text, seq: this.props.vacancy.questions.length },
            ],
        });
        this.props.onUpdate(vacancy);
    }

    private handleOnChangeQuestion(question: Question, text: string) {
        if (text.length === 0) {
            // remove question
            const vacancy: Vacancy = Object.assign({}, this.props.vacancy, {
                questions: this.props.vacancy.questions
                    .filter((q) => q.id !== question.id)
                    .sort(sortQuestions)
                    .map((q, index) => {
                        q.seq = index;
                        return q;
                    }),
            });
            this.props.onUpdate(vacancy);
        } else {
            // update question
            const vacancy: Vacancy = Object.assign({}, this.props.vacancy, {
                questions: this.props.vacancy.questions.map((q) => (q.id === question.id ? { ...q, text } : q)),
            });
            this.props.onUpdate(vacancy);
        }
    }

    private handleOnAddCandidate(firstname: string, lastname: string, email: string) {
        this.props.onAddCandidate(this.props.vacancy.id, firstname, lastname, email);
    }

    private handleOnUpdateCandidate(candidate: Candidate) {
        this.props.onUpdateCandidate(this.props.vacancy.id, candidate);
    }

    private handleOnDeleteCandidate(candidate: Candidate) {
        this.props.onDeleteCandidate(this.props.vacancy.id, candidate);
    }

    private handleOnInviteCandidate(candidate: Candidate) {
        this.props.onInviteCandidate(this.props.vacancy.id, candidate);
    }

    private handleOnUpdateHiringMangers(hiringManagers: HiringManager[]) {
        const hiringManagersWithoutDeleted = hiringManagers.filter((hm) => hm.email);
        const vacancy: Vacancy = Object.assign({}, this.props.vacancy, {
            hiringManagers: hiringManagersWithoutDeleted,
        });
        this.props.onUpdate(vacancy);
    }

    private handleOnShareWithHiringManager() {
        this.props.onShareWithHiringManager(this.props.vacancy);
    }

    private handleOnInviteAllCandidates() {
        this.props.vacancy.candidates.forEach((c) => {
            if (c.applicationState === ApplicationState.CREATED) {
                this.handleOnInviteCandidate(c);
            }
        });
    }

    private sharedWithHiringManagerText() {
        const notifiedHiringManagers = this.props.vacancy.hiringManagers.filter((hm) => hm.shareDate);
        if (notifiedHiringManagers.length === 0) {
            return 'not yet shared with hiring manager';
        } else {
            return 'shared with ' + notifiedHiringManagers.map((hm) => hm.email).join(', ');
        }
    }

    private handleOnAddVacancyLink() {
        this.props.onAddVacancyLink(this.props.vacancy);
    }

    public render() {
        const vacancy = this.props.vacancy;
        return (
            <Box>
                <Row>
                    <Name id="name" name="name" defaultValue={vacancy.name} onBlur={this.handleOnChangeName} />
                    <div>
                        <Row justify="flex-end">
                            <ShareWithHiringManagerButton
                                onShare={this.handleOnShareWithHiringManager}
                                hiringManagers={vacancy.hiringManagers}
                                onHiringManagersUpdate={this.handleOnUpdateHiringMangers}
                            />
                            <InviteAllCandidatesButton onInviteAllCandidates={this.handleOnInviteAllCandidates} />
                            <ShareVacancyButton
                                vacancyLinks={vacancy.vacancyLinks}
                                onAddVacancyLink={this.handleOnAddVacancyLink}
                            />
                        </Row>

                        <Details>
                            created at: {vacancy.creationDate} - {this.sharedWithHiringManagerText()}
                            <br />
                            <Link to={`/hiring-manager/${vacancy.id}`}>Hiring Manager Page</Link>
                        </Details>
                    </div>
                </Row>

                <h3>Candidates</h3>
                <Candidates>
                    {vacancy.candidates.map((c) => (
                        <VacancyCandidate
                            key={c.id}
                            candidate={c}
                            onUpdate={this.handleOnUpdateCandidate}
                            onInvite={this.handleOnInviteCandidate}
                            onRemove={this.handleOnDeleteCandidate}
                        />
                    ))}
                    <AddCandidateButton onAdd={this.handleOnAddCandidate} />
                </Candidates>

                <h3>Questions</h3>
                {vacancy.questions.sort(sortQuestions).map((q) => (
                    <VacancyQuestion
                        key={q.id}
                        question={q.text}
                        onChange={(text) => this.handleOnChangeQuestion(q, text)}
                    />
                ))}
                <VacancyQuestion key="new-question" question="" onChange={this.handleOnAddQuestion} />

                <Row justify="flex-end">
                    <DeleteVacancyButton name={vacancy.name} onOk={this.handleOnDelete} />
                </Row>
            </Box>
        );
    }
}

const sortQuestions = (q1: Question, q2: Question) => q1.seq - q2.seq;

export default VacancyBox;



// WEBPACK FOOTER //
// ./src/scenes/administrator/components/VacancyBox.tsx